import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaBoxOpen, FaShoppingCart, FaClipboardList, FaHourglassHalf, 
  FaCheckCircle, FaTruck, FaChartBar, FaMoneyBillWave, FaCalendarAlt,
  FaBan
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { fetchUnverifiedOrders, fetchVerifiedOrders } from '../api/api';

const DashboardPage: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAgent, setIsAgent] = useState<boolean>(false);

  // 1. Role Check
  useEffect(() => {
    const checkUserRole = () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));
                const payload = JSON.parse(jsonPayload);
                if (JSON.stringify(payload).toUpperCase().includes('AGENT')) {
                    setIsAgent(true);
                }
            }
        } catch (e) { console.error("Error decoding token", e); }
    };
    checkUserRole();
  }, []);

  // 2. Database se data lana (Merge Logic)
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // CRM Orders (Dono APIs se, kyunki Agent dashboard pe sab dekhna chahta hai)
      const [unverifiedRes, verifiedRes] = await Promise.all([
         fetchUnverifiedOrders().catch(() => ({ data: [] })),
         fetchVerifiedOrders().catch(() => ({ data: [] }))
      ]);

      // CRM Orders ko Map karna normal structure me
      const crmOrders = [...(unverifiedRes.data || []), ...(verifiedRes.data || [])].map((o: any) => ({
         id: o.id,
         orderDate: o.createdAt || new Date().toISOString(), // Fallback
         totalAmount: o.payableAmount || 0,
         status: o.status || 'PLACED',
         delhiveryWaybill: o.waybill || null, // 1. YEH LINE ADD KI: CRM orders ka waybill nikalne ke liye
         paymentStatus: o.status === 'DELIVERED' ? 'PAID' : 'PENDING',
         user: { firstName: o.mainUser?.firstName || o.customerName || 'CRM Customer', lastName: o.mainUser?.lastName || '' },
         source: 'CRM'
      }));

      let finalOrders = [];

      if (isAgent) {
         // Agent ko sirf uske lagaye CRM orders dikhenge
         finalOrders = crmOrders;
      } else {
         // Admin ko CRM ke sath Website Orders bhi dikhenge
         const webRes = await axios.get('https://www.ayushvatika.com/api/orders/all', {
            headers: { 'Authorization': `Bearer ${token}` }
         }).catch(() => ({ data: [] }));
         
         const webOrders = (webRes.data || []).map((o: any) => ({ ...o, source: 'WEBSITE' }));
         finalOrders = [...webOrders, ...crmOrders];
      }

      setOrders(finalOrders);

      // 2. YEH PURA BLOCK ADD KIYA: Delhivery se live tracking uthane ke liye (Same as CompletedOrder.tsx)
      const shippedOrders = finalOrders.filter(o => o.delhiveryWaybill && !['DELIVERED', 'CANCELLED', 'RTO'].includes(o.status?.toUpperCase()));
      if (shippedOrders.length > 0) {
          (async () => {
              for (const order of shippedOrders) {
                  try {
                      const res = await axios.post('https://www.ayushvatika.com/api/delivery/track', 
                          { waybill: order.delhiveryWaybill }, 
                          { headers: { Authorization: `Bearer ${token}` } }
                      );
                      if (res.data && res.data.rawResponse) {
                          const raw = JSON.parse(res.data.rawResponse);
                          let liveStatus = raw?.ShipmentData?.[0]?.Shipment?.Status?.Status?.toUpperCase();
                          if (liveStatus) {
                              if(liveStatus === 'IN TRANSIT') liveStatus = 'IN_TRANSIT';
                              if(liveStatus === 'OUT FOR DELIVERY') liveStatus = 'OUT_FOR_DELIVERY';
                              setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: liveStatus } : o));
                          }
                      }
                  } catch(e) { console.error(e); }
              }
          })();
      }

    } catch (err) {
      toast.error("Dashboard data load karne mein error aayi!");
    } finally {
      setLoading(false);
    }
  };

  const rtoCount = useMemo(() => {
  return orders.filter(order => order.orderStatus === 'REFUND' || order.orderStatus === 'RTO').length;
}, [orders]);


  useEffect(() => {
    fetchDashboardData();
  }, [isAgent]); // Dependency me isAgent pass kiya

  // 3. Weekly Graph Logic
  const weeklyData = useMemo(() => {
    const now = new Date();
    const todayStr = now.toLocaleDateString('en-CA'); 

    const currentDay = now.getDay(); 
    const diff = now.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);

    const weekDays = [];
    const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      
      const dateKey = d.toLocaleDateString('en-CA'); 
      const displayDate = d.getDate() + "/" + (d.getMonth() + 1);

      const dayTotal = orders
        .filter(o => {
          if (!o.orderDate) return false; 
          const oDatePart = o.orderDate.split('T')[0];
          // CRM ko bhi sale manna hai agar Verified/Delivered hai
          const isPaidOrConfirmed = o.paymentStatus === 'PAID' || o.status === 'DELIVERED' || o.source === 'CRM';
          return oDatePart === dateKey && isPaidOrConfirmed;
        })
        .reduce((sum, o) => sum + o.totalAmount, 0);

      weekDays.push({ 
        dayName: labels[i], 
        date: displayDate, 
        amount: dayTotal,
        isToday: dateKey === todayStr 
      });
    }
    return weekDays;
  }, [orders]);

  const maxAmount = Math.max(...weeklyData.map(d => d.amount), 1);

  // 4. Stats Logic
  const stats = useMemo(() => {
    const now = new Date();
    const todayStr = now.toLocaleDateString('en-CA');
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    const yesterdayStr = yesterday.toLocaleDateString('en-CA');
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const lastMonthDate = new Date();
    lastMonthDate.setMonth(now.getMonth() - 1);
    const lastMonth = lastMonthDate.getMonth();
    const lastMonthYear = lastMonthDate.getFullYear();

    let tSale = 0, ySale = 0, tmSale = 0, lmSale = 0, allSale = 0;
    let pend = 0, proc = 0, deliv = 0;

    orders.forEach(o => {
      if (!o.orderDate) return; 

      const oDateStr = o.orderDate.split('T')[0];
      const oDate = new Date(o.orderDate);
      const isPaidOrConfirmed = o.paymentStatus === 'PAID' || o.status === 'DELIVERED' || o.source === 'CRM';

      if (isPaidOrConfirmed) {
        allSale += o.totalAmount;
        if (oDateStr === todayStr) tSale += o.totalAmount;
        if (oDateStr === yesterdayStr) ySale += o.totalAmount;
        if (oDate.getMonth() === currentMonth && oDate.getFullYear() === currentYear) tmSale += o.totalAmount;
        if (oDate.getMonth() === lastMonth && oDate.getFullYear() === lastMonthYear) lmSale += o.totalAmount;
      }

     const currentStatus = o.status?.toUpperCase() || '';
      
      if (currentStatus === 'PLACED' || currentStatus === 'UNVERIFIED') pend++;
      if (currentStatus === 'SHIPPED' || currentStatus === 'OUT_FOR_DELIVERY' || currentStatus === 'VERIFIED' || currentStatus === 'MANIFESTED' || currentStatus === 'IN_TRANSIT') proc++;
      if (currentStatus === 'DELIVERED') deliv++;

    });

    return { tSale, ySale, tmSale, lmSale, allSale, pend, proc, deliv, total: orders.length };
  }, [orders]);

  if (loading) return <AdminLayout><div className="p-10 text-center font-bold text-gray-400 animate-pulse">Updating Dashboard Data...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
        
        {/* Top Sale Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <SalesCard title="Today Sale" amount={stats.tSale} icon={<FaShoppingCart/>} color="border-green-500" iconColor="text-green-500" />
            <SalesCard title="Yesterday Sale" amount={stats.ySale} icon={<FaBoxOpen/>} color="border-orange-500" iconColor="text-orange-500" />
            <SalesCard title="This Month" amount={stats.tmSale} icon={<FaClipboardList/>} color="border-teal-500" iconColor="text-blue-500" />
            <SalesCard title="Last Month" amount={stats.lmSale} icon={<FaCalendarAlt/>} color="border-purple-500" iconColor="text-purple-500" />
            <SalesCard title="All-Time Sales" amount={stats.allSale} icon={<FaMoneyBillWave/>} color="border-teal-500" iconColor="text-teal-500" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatusCard title="Total Orders" count={stats.total} bgColor="bg-orange-500" icon={<FaShoppingCart/>} />
            <StatusCard title="Pending" count={stats.pend} bgColor="bg-blue-500" icon={<FaHourglassHalf/>} />
            <StatusCard title="Processing" count={stats.proc} bgColor="bg-green-500" icon={<FaTruck/>} />
            <StatusCard title="Delivered" count={stats.deliv} bgColor="bg-teal-500" icon={<FaCheckCircle/>} />
            {/* <StatusCard title="RTO / Returned" count={rtoCount} bgColor="bg-rose-600" icon={<FaBan />} /> */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Graph Section */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-8 rounded-[2.5rem] shadow-xl border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white transition-colors duration-300">
            <div className="flex justify-between items-center mb-10">
                <h2 className="text-xl font-black uppercase tracking-widest flex items-center gap-3">
                    <FaChartBar className="text-green-500 dark:text-green-400"/> Weekly Sales Flow
                </h2>
                <span className="text-[10px] bg-gray-100 dark:bg-gray-700 px-4 py-1.5 rounded-full text-gray-600 dark:text-gray-400 font-black tracking-widest border border-gray-200 dark:border-gray-600 transition-colors">MON - SUN</span>
            </div>

            <div className="h-72 flex items-end justify-between gap-4 px-2">
              {weeklyData.map((item, i) => (
                <div key={i} className="flex flex-col items-center flex-1 group relative">
                  <div className="absolute -top-12 bg-gray-900 text-white dark:bg-white dark:text-gray-900 text-[11px] font-black px-3 py-1.5 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0 z-20">
                    ₹{item.amount.toLocaleString()}
                  </div>
                  <div 
                    className={`w-full rounded-t-2xl transition-all duration-1000 shadow-sm group-hover:shadow-lg ${
                        item.isToday ? 'bg-gradient-to-t from-blue-600 to-blue-400 scale-105' : 'bg-gradient-to-t from-green-500 to-green-300 dark:from-green-600 dark:to-green-400'
                    } group-hover:brightness-110`} 
                    style={{ height: `${(item.amount / maxAmount) * 100}%`, minHeight: item.amount > 0 ? '12px' : '6px' }}
                  ></div>
                  <div className="mt-5 text-center">
                    <p className={`text-[11px] font-black tracking-tighter ${item.isToday ? 'text-blue-600 dark:text-blue-400 underline underline-offset-4' : 'text-gray-500 dark:text-gray-400'}`}>{item.dayName}</p>
                    <p className="text-[9px] text-gray-400 dark:text-gray-500 font-bold mt-0.5">{item.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-800 p-8 rounded-[2.5rem] shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-300">
            <h2 className="text-xl font-black text-gray-800 dark:text-white mb-8 tracking-tight">Recent Activity</h2>
            <div className="space-y-5">
              {orders.sort((a,b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()).slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-100 dark:border-gray-600 hover:scale-[1.02] transition-all cursor-pointer shadow-sm">
                  <div>
                    <p className="text-[10px] font-black text-blue-600 dark:text-blue-400 font-mono tracking-tighter uppercase">
                        {order.source === 'CRM' ? 'CRM Order' : 'Web Order'}
                    </p>
                    <p className="text-sm font-black text-gray-800 dark:text-gray-200 mt-0.5 truncate w-24">{order.user ? order.user.firstName : 'Unknown User'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black text-gray-900 dark:text-white">₹{order.totalAmount}</p>
                    <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter ${
                      order.status === 'DELIVERED' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

const SalesCard = ({title, amount, icon, color, iconColor}: any) => (
  <div className={`bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border-t-4 ${color} hover:shadow-xl transition-all transform hover:-translate-y-1 border-gray-100 dark:border-gray-700`}>
    <div className="flex justify-between items-start mb-3">
      <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em]">{title}</span>
      <span className={`text-xl ${iconColor}`}>{icon}</span>
    </div>
    <div className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">₹{amount.toLocaleString('en-IN')}</div>
  </div>
);

const StatusCard = ({title, count, bgColor, icon}: any) => (
  <div className="bg-slate-800 dark:bg-slate-800 p-6 rounded-[2rem] shadow-xl flex items-center gap-5 text-white group hover:bg-slate-700 transition-colors border border-slate-700 dark:border-gray-600">
    <div className={`${bgColor} w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-2xl transition-transform group-hover:rotate-12`}>
      {icon}
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">{title}</p>
      <p className="text-3xl font-black tracking-tighter">{count}</p>
    </div>
  </div>
);

export default DashboardPage;